<template>
  <GMapMap
    ref="map"
    :center="center"
    :zoom="16"
    :max-zoom="10"
    :options="defaultOptions"
    class="h-fit"
    map-type-id="roadmap"
  >
    <template v-for="point in points" :key="`${point.gisLocation.lat}.${point.gisLocation.long}`">
      <GMapInfoWindow
        v-if="showInfo"
        :opened="shownPoints[point.id] ?? false"
        :options="{
          position: {
            lat: point.gisLocation.lat,
            lng: point.gisLocation.long,
          },
        }"
        :closeclick="true"
        @closeclick="setShownPoint(point, false)"
      >
        <div class="info">
          <slot name="info" v-bind="{ point }" />
        </div>
      </GMapInfoWindow>
      <GMapCircle
        v-if="!hideInnerCircle && point.gisLocation && point.value"
        :radius="
          mapNumber(point.value ?? 0, low, high, point.radius * 1.5, point.radius * 2.5)
        "
        :center="{
          lat: point.gisLocation?.lat,
          lng: point.gisLocation?.long,
        }"
        :options="{
          clickable: false,
          strokeWeight: 0,
          fillColor: '#7B61FF',
          fillOpacity: 0.3,
        }"
      />
      <GMapCircle
        v-if="point.gisLocation"
        :radius="point.radius"
        :center="{
          lat: point.gisLocation?.lat,
          lng: point.gisLocation?.long,
        }"
        :options="{
          clickable: true,
          strokeWeight: 0,
          fillColor: '#B63D44',
          fillOpacity: 0.8,
        }"
        @click="setShownPoint(point, true)"
      />
    </template>
  </GMapMap>
</template>

<script lang="ts" setup>
import Box from '@components/utils/Box.vue';
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { defaultCenter, defaultOptions } from '@/utils/map';
import type { Location } from '@/entities/Entities';

interface LocationPoint {
  id: string;
  gisLocation: Location;
  radius: number;
  value?: number;
}

interface Props {
  points?: LocationPoint[];
  showInfo?: boolean;
  hideInnerCircle?: boolean;
}

const props = defineProps<Props>();
const map = ref();
const low = ref();
const high = ref();
const center = ref(defaultCenter);
const shownPoints = ref<Record<string, boolean>>({});
const points = computed(() => props.points?.filter((point) => point.gisLocation));

const setShownPoint = (point: LocationPoint, shown = false) => {
  shownPoints.value[point.id] = shown;
};

const mapNumber = (s: number, a1: number, a2: number, b1: number, b2: number) =>
  b1 + ((s - a1) * (b2 - b1)) / (a2 - a1);

const LAT_PER_METER = 1.0 / 111000;
const LONG_PER_METER = 1.0 / 76000;

const computeCenter = () => {
  if (points.value?.length) {
    // eslint-disable-next-line no-undef
    const bounds = new google.maps.LatLngBounds();
    for (const point of points.value) {
      bounds.extend({
        lat: point.gisLocation.lat + LAT_PER_METER * point.radius,
        lng: point.gisLocation.long + LONG_PER_METER * point.radius,
      });
      bounds.extend({
        lat: point.gisLocation.lat - LAT_PER_METER * point.radius,
        lng: point.gisLocation.long - LONG_PER_METER * point.radius,
      });
      bounds.extend({
        lat: point.gisLocation.lat - LAT_PER_METER * point.radius,
        lng: point.gisLocation.long + LONG_PER_METER * point.radius,
      });
      bounds.extend({
        lat: point.gisLocation.lat + LAT_PER_METER * point.radius,
        lng: point.gisLocation.long - LONG_PER_METER * point.radius,
      });
    }
    center.value = bounds.getCenter();
    map.value.fitBounds(bounds);
  } else {
    center.value = defaultCenter;
  }
};

const updateLowHigh = () => {
  let [localLow, localHigh] = [Infinity, 0];
  for (const point of props?.points ?? []) {
    const val = point.value ?? 0;
    if (val > localHigh) localHigh = point.value ?? 0;
    if (val < localLow) localLow = point.value ?? 0;
  }
  if (localLow === localHigh) {
    localLow = localHigh - 1;
  }

  low.value = localLow;
  high.value = localHigh;
};

watch(
  () => props.points,
  () => {
    computeCenter();
    updateLowHigh();
  },
);

onMounted(async () => {
  await map.value.$mapPromise;
  computeCenter();
  updateLowHigh();
});

/*
const setGisLocation = (event: google.maps.MapMouseEvent) => {
  if (event.latLng) {
    value.value = {
      lat: event.latLng?.lat(),
      long: event.latLng?.lng(),
    };
  }
};

const setRadius = (rad: number) => {
  if (radius.value !== rad) {
    radius.value = Math.round(rad);
  }
};

const setCenter = (center: google.maps.LatLng) => {
  if (value.value?.lat !== center.lat() || value.value?.long !== center?.lng()) {
    value.value = {
      lat: center?.lat(),
      long: center?.lng(),
    };
  }
};
*/
</script>
<style lang="scss" scoped>
:deep(.info) {
  h6 {
    font-size: 16px;
    padding-bottom: 0.5rem;
    font-weight: 700;
  }
}
</style>
